import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, keyframes, Slider, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { AccentIconTile } from "../../../components/UIToolkit/molecules/AccentIconTile";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { MOTION } from "../../../theme/tokens";
import type { PbjModelUpsertBody, PbjPrediction } from "../../../utils/models";
import { PBJ_ALPHA } from "./colors";
import { effectiveBaselineForPrefix, scoreGapFromBaseline } from "./cumulativeScoring.utils";
import type { PeriodMode } from "./RecommendationsSidebar";
import { buildPlanItem, calculateRecData, displaySpendForLabel, MAX_HPPD_PER_RECOMMENDATION } from "./recMath.utils";
import { type PlanItem, type RecommendationType, WAGE_OVERRIDE_FIELDS, type WageKey, type WageOverrideField } from "./recommendations.constants";

// One-shot land-pulse for a slider the Suggest action just repositioned: a brief halo
// ring grows + fades on the thumb (~320ms, ease-out) so the eye catches the move. GPU
// props only (box-shadow spread); honored conditionally below so it never fires on
// untouched or user-dragged sliders. Reduced-motion strips it. The pulse color is
// injected at render time (theme-derived) via a CSS var so the keyframe stays static.
const SLIDER_LAND_PULSE = keyframes`
	0% { box-shadow: 0 0 0 0 var(--pbj-pulse); }
	60% { box-shadow: 0 0 0 6px var(--pbj-pulse); }
	100% { box-shadow: 0 0 0 0 transparent; }
`;

// wageKey → the override field that pins it. Lets each rec's wage tooltip report
// whether the wage shown is a manually-set override or the computed assumption.
const OVERRIDE_KEY_BY_WAGE_KEY = Object.fromEntries(WAGE_OVERRIDE_FIELDS.map((f) => [f.computedKey, f.overrideKey])) as Record<
	WageKey,
	WageOverrideField["overrideKey"]
>;

// Base i18n stem per role; an overridden wage appends "_override" to swap the
// "Assumed …" copy for the "manually set" variant.
const wageTooltipKey = (rec: RecommendationType, isOverride: boolean): string => {
	const role = rec.wageKey === "rn" || rec.wageKey === "weekendRn" ? "rn" : rec.wageKey === "lpn" || rec.wageKey === "weekendLpn" ? "lpn" : "cna";
	return `pbj_model.recommendations.wage_tooltip.${role}${isOverride ? "_override" : ""}`;
};

type Props = {
	rec: RecommendationType;
	// Assumed hourly wage for this lever's role (drives the slider's HPPD↔spend math).
	wage: number;
	// Theme-derived categorical accent for this lever (resolved by the parent via recColor).
	color: string;
	// The plan item when this rec is in the Action Plan; undefined otherwise.
	selectedItem: PlanItem | undefined;
	// Live slider spend (plan value when selected, locally-tracked spendMap value otherwise).
	currentSpend: number;
	// Latest Suggest positioned this rec (spend > 0) and it's not yet added — drives the
	// "Suggested" badge, the faint wash + accent rail, and the one-shot slider land-pulse.
	isSuggested: boolean;
	// No spend dialed in and not in the plan — the slider reads as "nothing requested".
	isInert: boolean;
	prediction: PbjPrediction;
	census: number;
	// The current Action Plan — the prefix the cumulative re-score attributes points against.
	selectedPlan: PlanItem[];
	periodMode: PeriodMode;
	// Day denominators for the math tooltip, resolved for the active period mode by the parent.
	periodDays: number;
	periodWeekendDays: number;
	// The live config draft — read only to tell the wage tooltip override vs. computed.
	draft: PbjModelUpsertBody;
	onAdd: (item: PlanItem) => void;
	onSlider: (rec: RecommendationType, newSpend: number) => void;
};

/**
 * A single recommendation lever card in the "How" sidebar: icon + title (with a math
 * tooltip), a spend/PPD slider, and the projected HPPD impact / cost / points with an
 * Add (or "Active") affordance. The per-rec cumulative re-score — attributing this
 * lever's marginal points over the effective baseline of the plan prefix before it —
 * lives here so the parent keeps only state + the shared handlers.
 */
export const RecommendationRow: FC<Props> = ({
	rec,
	wage,
	color,
	selectedItem,
	currentSpend,
	isSuggested,
	isInert,
	prediction,
	census,
	selectedPlan,
	periodMode,
	periodDays,
	periodWeekendDays,
	draft,
	onAdd,
	onSlider,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const accent = theme.palette.primary.main;
	const brandTintC = alpha(accent, PBJ_ALPHA.TINT);
	const brandBorderC = alpha(accent, PBJ_ALPHA.BORDER);
	const brandFaintC = alpha(accent, PBJ_ALPHA.FAINT);

	const isSelected = !!selectedItem;
	// When the rec sits in the plan, the slider and impact/points labels reflect the plan
	// item's stored values verbatim (BE-suggested or previously slider-adjusted). Otherwise
	// project via FE math from the current spend.
	const feData = calculateRecData(rec, currentSpend, wage, prediction, census);
	// Per-category HPPD gaps for this rec at its current spend. These are baseline-INDEPENDENT
	// (depend only on hours/census/days/scale), so we use the plan item's stored breakdown when
	// it's in the plan (BE-suggested or slider-adjusted) and the freshly-projected FE one otherwise.
	const gapBreakdown = selectedItem?.breakdown ?? feData.breakdown;
	// Effective baseline = rawValue + cumulative shift of the prefix of OTHER selected items added
	// BEFORE this rec. For a not-yet-selected rec the prefix is the entire current plan; for a
	// selected rec it's the items before it (its own shift excluded via `stopId`). Scoring this
	// rec's gaps against that baseline (instead of raw `cat.rawValue`) makes the "+X pts" preview
	// equal this rec's Action Plan row and removes the over/under-credit from static-baseline
	// scoring. Mirrors the BE greedy's add-order prefix attribution (`_to_steps`).
	const effectiveBaseline = effectiveBaselineForPrefix(selectedPlan, prediction.categories, selectedItem ? rec.id : undefined);
	// Re-score each touched category's marginal point gain over the effective baseline, then
	// advance the local running value to the same nudged point the gain was scored at
	// (scoreGapFromBaseline mirrors the BE `_to_steps` nudge-then-score order) so two categories
	// of the same rec don't double-credit a shared band edge and a BE-suggested gap landing a
	// hair below its cutoff still credits the crossing.
	const scoredBreakdown = gapBreakdown.map((b) => {
		const cat = prediction.categories.find((c) => c.id === b.categoryId);
		if (!cat) return { ...b, points: 0 };
		const before = effectiveBaseline[b.categoryId] ?? cat.rawValue;
		const { gain, after } = scoreGapFromBaseline(cat, before, b.hppdGap);
		effectiveBaseline[b.categoryId] = after;
		return { ...b, points: gain };
	});
	const cumulativePoints = scoredBreakdown.reduce((sum, b) => sum + b.points, 0);
	// HPPD `impact`, `hours`, `hoursCatchup`, spend and cost stay as-is (plan-item values when
	// selected, FE projection otherwise) — only the POINTS are re-scored cumulatively.
	const data = {
		impact: selectedItem?.impact ?? feData.impact,
		points: cumulativePoints,
		hours: selectedItem?.hours ?? feData.hours,
		hoursCatchup: selectedItem ? selectedItem.hoursCatchup : feData.hoursCatchup,
		breakdown: scoredBreakdown,
	};

	return (
		<Box
			sx={{
				p: 2,
				// An in-plan row is marked by a solid accent rail, not a full-row wash: a brand-tint fill
				// here is the same tone as the panel header + page canvas, so with most levers active the
				// panel flattened into one block. Only the transient suggested state carries a wash.
				bgcolor: isSuggested ? brandFaintC : "transparent",
				// Reserve the rail width on every card (transparent when off) so the
				// suggested tint doesn't shift the card content sideways.
				borderLeft: "3px solid",
				borderLeftColor: isSelected ? accent : isSuggested ? brandBorderC : "transparent",
				transition: `background-color ${MOTION.DURATION.COLOR_SHIFT}ms, border-color ${MOTION.DURATION.COLOR_SHIFT}ms`,
				"@media (prefers-reduced-motion: reduce)": { transition: "none" },
			}}
			data-testid={`pbj-model-recommendation-${rec.id}`}
		>
			<Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" } }}>
				{/* Hover the icon or title to see this rec's math (how added hours map to
				    HPPD across the categories it touches, with real period day-counts and
				    the weekend 2/7 ratio called out where it applies). */}
				<Tooltip
					arrow
					placement="bottom-start"
					slotProps={{ tooltip: { sx: { maxWidth: 360, p: 1.25 } } }}
					title={
						<Box data-testid={`pbj-model-recommendation-math-${rec.id}`}>
							<Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
								{t(rec.titleKey)}
							</Typography>
							<Typography variant="caption" component="div" sx={{ display: "block", lineHeight: 1.45 }}>
								{t(rec.mathKey, {
									days: periodDays,
									weekendDays: periodWeekendDays,
									ratio: periodWeekendDays > 0 ? (periodDays / periodWeekendDays).toFixed(1) : "—",
									census,
								})}
							</Typography>
							<Typography variant="caption" component="div" sx={{ display: "block", mt: 0.75, opacity: 0.75, fontStyle: "italic" }}>
								{t(periodMode === "quarter" ? "pbj_model.recommendations.period.quarter_hint" : "pbj_model.recommendations.period.qtd_hint")}
							</Typography>
						</Box>
					}
				>
					<Stack
						direction="row"
						spacing={1.5}
						sx={{ alignItems: "center", flex: 1, minWidth: 0, cursor: "help" }}
						data-testid={`pbj-model-recommendation-info-${rec.id}`}
					>
						<AccentIconTile size="xs" color={color} tint={alpha(color, PBJ_ALPHA.TINT)}>
							{rec.icon}
						</AccentIconTile>
						<Box sx={{ minWidth: 0 }}>
							<Stack direction="row" spacing={0.75} sx={{ alignItems: "center", minWidth: 0 }}>
								<Typography variant="body2" noWrap sx={{ fontWeight: "bold" }}>
									{t(rec.titleKey)}
								</Typography>
								{/* "Suggested" pill — same highlight tokens as the panel header chip, so a
								    positioned-but-not-added rec is legible at a glance. */}
								{isSuggested && (
									<Typography
										component="span"
										variant="caption"
										sx={{
											flexShrink: 0,
											px: 0.75,
											py: 0.125,
											borderRadius: 1,
											bgcolor: brandTintC,
											color: accent,
											fontWeight: 700,
											fontSize: "0.6rem",
											lineHeight: 1.4,
											textTransform: "uppercase",
											letterSpacing: 0.3,
										}}
										data-testid={`pbj-model-recommendation-suggested-badge-${rec.id}`}
									>
										{t("pbj_model.recommendations.suggested_badge")}
									</Typography>
								)}
							</Stack>
							<Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2, mt: 0.25 }}>
								{t(rec.descKey)}
							</Typography>
						</Box>
					</Stack>
				</Tooltip>
				<Box sx={{ px: 1, flex: 1, minWidth: 0 }}>
					<Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 0 }}>
						<Stack direction="row" sx={{ alignItems: "center" }}>
							<Typography variant="caption" color="text.secondary">
								{t("pbj_model.recommendations.spend_ppd")}
							</Typography>
							<Tooltip title={t(wageTooltipKey(rec, draft[OVERRIDE_KEY_BY_WAGE_KEY[rec.wageKey]] != null), { value: wage.toFixed(2) })} arrow>
								<InfoOutlinedIcon sx={{ fontSize: 12, color: "text.secondary", ml: 0.5, cursor: "help" }} />
							</Tooltip>
						</Stack>
						{/* Spend PPD label is normalized to a per-CALENDAR-day rate over the
						    window the period toggle is measuring (full quarter in "quarter"
						    mode, remaining days in "qtd" mode). This makes weekday and weekend
						    recs directly comparable AND matches the Action Plan's per-item
						    Spend PPD line. The slider's underlying value stays in raw
						    $/applicable-PPD units — only the label is normalized. */}
						<Tooltip
							arrow
							placement="top"
							title={
								rec.isWeekend
									? t("pbj_model.action_plan.weekend_spend_tooltip", {
											value: currentSpend.toFixed(2),
											weekendDays: prediction.quarterWeekendDays,
											quarterDays: prediction.quarterDays,
										})
									: ""
							}
						>
							<Typography variant="caption" sx={{ fontWeight: "bold", color: color, cursor: rec.isWeekend ? "help" : "default" }}>
								{t("pbj_model.recommendations.spend_format", { value: displaySpendForLabel(rec, currentSpend, prediction, periodMode).toFixed(2) })}
							</Typography>
						</Tooltip>
					</Stack>
					{/* Slider max corresponds to the maximum HPPD impact you can dial in:
					    `impact = spend / wage`, so `max = wage × MAX_HPPD_PER_RECOMMENDATION`
					    caps impact at that HPPD. The BE greedy enforces the same cap on each
					    suggested step's primary-cat HPPD so BE values fit within the slider.
					    `Math.max(…, currentSpend)` is a defensive guard against future BE drift
					    (e.g. cap raised on one side and not the other). */}
					<Slider
						// Re-key on the suggested value so the land-pulse keyframe replays each time
						// Suggest repositions this thumb (React remounts → animation runs once).
						key={isSuggested ? `suggested-${currentSpend.toFixed(2)}` : "manual"}
						size="small"
						value={currentSpend}
						min={0}
						max={Math.max(wage * MAX_HPPD_PER_RECOMMENDATION, currentSpend)}
						step={0.01}
						onChange={(_, val) => onSlider(rec, Array.isArray(val) ? val[0] : val)}
						sx={{
							color: color,
							py: 0.5,
							"--pbj-pulse": brandTintC,
							// An untouched (spend 0, not in plan) slider reads as inert: muted rail,
							// no thumb halo. A positioned/selected one shows the rec color at full strength.
							"& .MuiSlider-rail": { opacity: isInert ? 0.25 : 0.38 },
							"& .MuiSlider-track": { opacity: isInert ? 0.4 : 1 },
							"& .MuiSlider-thumb": {
								transition: `box-shadow ${MOTION.DURATION.COLOR_SHIFT}ms`,
								"@media (prefers-reduced-motion: reduce)": { transition: "none" },
								...(isSuggested
									? {
											animation: `${SLIDER_LAND_PULSE} ${MOTION.DURATION.PULSE}ms ${MOTION.EASE_OUT}`,
											"@media (prefers-reduced-motion: reduce)": { animation: "none", transition: "none" },
										}
									: {}),
							},
						}}
						aria-label={t("pbj_model.recommendations.spend_slider_aria", { title: t(rec.titleKey) })}
						data-testid={`pbj-model-recommendation-slider-${rec.id}`}
					/>
				</Box>
				<Stack direction="row" spacing={1.5} sx={{ alignItems: "center", justifyContent: "space-between", flex: 1, minWidth: 0 }}>
					<Box>
						<Typography variant="caption" sx={{ fontWeight: "bold", display: "block" }}>
							{t("pbj_model.recommendations.impact_hppd", { value: (Math.floor(data.impact * 1000) / 1000).toFixed(3) })}
						</Typography>
						{/* Per-rec projected dollar cost — `spend × census × applicableQuarterDays`.
						    Mirrors the per-item `itemPrice` in ActionItems so the value the user sees
						    here matches the row in the Action Plan once they add the rec. */}
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ fontWeight: 600, display: "block" }}
							data-testid={`pbj-model-recommendation-cost-${rec.id}`}
						>
							{t("pbj_model.recommendations.cost_total", {
								value: (currentSpend * census * (rec.isWeekend ? prediction.quarterWeekendDays : prediction.quarterDays)).toLocaleString(undefined, {
									maximumFractionDigits: 0,
								}),
							})}
						</Typography>
						{/* Hover tooltip on the +X pts label itemizes per-bucket point gains
						    AND per-bucket HPPD bumps. Only shown for recs that touch more than
						    one HPPD category. Weekend recs see different HPPD bumps per cat
						    (Weekend HPPD vs. Total/RN HPPD diverge because hours only land on
						    weekend shifts), so the per-cat HPPD column makes that visible. */}
						<Tooltip
							arrow
							placement="top"
							title={
								data.breakdown.length > 1 ? (
									<Box sx={{ py: 0.25 }} data-testid={`pbj-model-recommendation-points-breakdown-${rec.id}`}>
										{data.breakdown.map((b) => (
											<Box key={b.categoryId} sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, fontSize: "0.7rem", lineHeight: 1.4 }}>
												<span>{b.categoryTitle}</span>
												<span style={{ fontWeight: 600 }}>
													+{b.hppdGap.toFixed(3)} HPPD · {t("pbj_model.recommendations.impact_points", { count: b.points })}
												</span>
											</Box>
										))}
									</Box>
								) : (
									""
								)
							}
						>
							<Typography
								variant="caption"
								sx={{ fontWeight: "bold", color: color, cursor: data.breakdown.length > 1 ? "help" : "default" }}
								data-testid={`pbj-model-recommendation-points-${rec.id}`}
							>
								{t("pbj_model.recommendations.impact_points", { count: data.points })}
							</Typography>
						</Tooltip>
					</Box>
					{isSelected ? (
						<Stack direction="row" spacing={0.5} sx={{ alignItems: "center", color: "success.main" }}>
							<CheckCircleIcon sx={{ fontSize: 16 }} />
							<Typography variant="caption" sx={{ fontWeight: "bold" }}>
								{t("pbj_model.recommendations.active")}
							</Typography>
						</Stack>
					) : (
						// Per-category accent outlined button. No System* wrapper covers an
						// arbitrary categorical color, so this stays MUI — but the color is
						// now theme-derived (via recColor), never a hard-coded hex.
						<SystemButton
							buttonVariant="secondary"
							size="small"
							onClick={() => onAdd(buildPlanItem(rec, currentSpend, data))}
							leadingIcon={<AddIcon sx={{ fontSize: 14 }} />}
							noFullWidthMobile
							sx={{
								minWidth: 90,
								color: color,
								borderColor: alpha(color, PBJ_ALPHA.BORDER),
								"&:hover": { bgcolor: alpha(color, PBJ_ALPHA.FAINT), borderColor: color },
							}}
							data-testid={`pbj-model-recommendation-add-${rec.id}`}
						>
							{t("pbj_model.recommendations.add")}
						</SystemButton>
					)}
				</Stack>
			</Stack>
		</Box>
	);
};
